import { Guid } from "guid-typescript";
import { CSSProperties, PureComponent, ReactNode } from "react";
import withCommonEvents from "../../../shared/hoc/with-common-events";
import { CommonProps } from "../common-props";
import "./radio-group.scss";

interface IRadioGroupProps {
  value?: string | string[];
  space?: number;
  selectedRadioColor?: string;
  unSelectedRadioColor?: string;
  direction?: "horizontal" | "vertical";
  style?: CSSProperties;
  onChange: (value: string | string[]) => void;
  children?: any;
  options?: any[];
  datatextfield?: string;
  datavaluefield?: any;
  multiSelectable?: boolean;
}

interface IRadioGroupState {
  uniqueKey: Guid;
}

class RadioGroup extends PureComponent<IRadioGroupProps & CommonProps, IRadioGroupState> {
  constructor(props: IRadioGroupProps) {
    super(props);
    this.state = {
      uniqueKey: Guid.create()
    };
  }

  private getStyles = () => {
    const style = { ...this.props.style };

    if (style) {
      if (style.display === "block") {
        style.display = "flex";
      } else {
        style.display = "inline-flex";
      }

      style.gap = this.props.space ?? "5px";

      if (this.props.direction === "horizontal") {
        style.flexDirection = "row";
        if ((style.textAlign as any) === "-webkit-center") {
          style.justifyContent = "center";
        } else if ((style.textAlign as any) === "-webkit-right") {
          style.justifyContent = "right";
        } else {
          style.justifyContent = "left";
        }
      } else {
        style.flexDirection = "column";
        style.justifyContent = style.alignItems;
        if ((style.textAlign as any) === "-webkit-center") {
          style.alignItems = "center";
        } else if ((style.textAlign as any) === "-webkit-right") {
          style.alignItems = "flex-end";
        } else if ((style.textAlign as any) === "-webkit-left") {
          style.alignItems = "flex-start";
        }
      }
    }

    return style;
  };

  private checkbox = (checkboxColor: string, isChecked: boolean) => {
    return (
      <div className="kuika-radio-group__radio__svgs">
        {isChecked ? (
          <svg
            className="kuika-radio-group__radio__checkbox-svg"
            focusable="false"
            aria-hidden="true"
            width={20}
            height={20}
            viewBox="0 0 24 24"
            fill={checkboxColor}
          >
            <path d="M19 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.11 0 2-.9 2-2V5c0-1.1-.89-2-2-2zm-9 14l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"></path>
          </svg>
        ) : (
          <svg
            className="kuika-radio-group__radio__checkbox-svg"
            focusable="false"
            aria-hidden="true"
            width={20}
            height={20}
            viewBox="0 0 24 24"
            fill="none"
            stroke={checkboxColor}
            strokeWidth="2"
          >
            <path d="M19 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.11 0 2-.9 2-2V5c0-1.1-.89-2-2-2z"></path>
          </svg>
        )}
      </div>
    );
  };

  private radio = (radioColor: string) => {
    return (
      <div className="kuika-radio-group__radio__svgs">
        <svg
          className="kuika-radio-group__radio__outer-svg"
          focusable="false"
          aria-hidden="true"
          width={20}
          height={20}
          viewBox="0 0 24 24"
          fill={radioColor}
        >
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z"></path>
        </svg>

        <svg
          className="kuika-radio-group__radio__inner-svg"
          width={20}
          height={20}
          focusable="false"
          aria-hidden="true"
          viewBox="0 0 24 24"
          fill={radioColor}
        >
          <path d="M8.465 8.465C9.37 7.56 10.62 7 12 7C14.76 7 17 9.24 17 12C17 13.38 16.44 14.63 15.535 15.535C14.63 16.44 13.38 17 12 17C9.24 17 7 14.76 7 12C7 10.62 7.56 9.37 8.465 8.465Z"></path>
        </svg>
      </div>
    );
  };

  private getNormalizedMultiValue = (): string[] => {
    const { value } = this.props;

    if (Array.isArray(value)) {
      return value;
    }

    if (typeof value === "string" && value.length > 0) {
      return value
        .split(",")
        .map((val) => val.trim())
        .filter((val) => val.length > 0);
    }

    return [];
  };

  private isOptionSelected = (optionKey: string): boolean => {
    const { value, multiSelectable: multiselectable } = this.props;

    if (multiselectable) {
      return this.getNormalizedMultiValue().includes(optionKey);
    }

    return value === optionKey;
  };

  private handleChange = (selectedValue: string) => {
    const { onChange, multiSelectable: multiselectable } = this.props;

    if (multiselectable) {
      const current = this.getNormalizedMultiValue();
      const newValue = current.includes(selectedValue)
        ? current.filter((val) => val !== selectedValue)
        : [...current, selectedValue];

      onChange(newValue);
    } else {
      onChange(selectedValue);
    }
  };

  private getRadioElements = () => {
    const {
      options,
      datavaluefield,
      datatextfield,
      multiSelectable: multiselectable,
      selectedRadioColor,
      unSelectedRadioColor
    } = this.props;

    const labelStyles: CSSProperties = {};

    if (this.props.style) {
      const { color, fontFamily, fontSize, fontStyle, fontWeight, letterSpacing, lineHeight, textTransform } =
        this.props.style;

      if (color) labelStyles.color = color;
      if (fontFamily) labelStyles.fontFamily = fontFamily;
      if (fontSize) labelStyles.fontSize = fontSize;
      if (fontStyle) labelStyles.fontStyle = fontStyle;
      if (fontWeight) labelStyles.fontWeight = fontWeight;
      if (letterSpacing) labelStyles.letterSpacing = letterSpacing;
      if (lineHeight) labelStyles.lineHeight = lineHeight;
      if (textTransform) labelStyles.textTransform = textTransform;
    }

    return options?.map((option, i) => {
      const { uniqueKey } = this.state;
      const radioGroupElementId = uniqueKey.toString();
      const radioElementId = `${radioGroupElementId}__radio-${i}`;
      const optionKey = datavaluefield ? option[datavaluefield] : option.key;
      const optionText = datatextfield ? option[datatextfield] : option.text;

      const isChecked = this.isOptionSelected(optionKey);
      const inputType = multiselectable ? "checkbox" : "radio";
      const colorToUse = !isChecked ? unSelectedRadioColor || "#616161" : selectedRadioColor || "#246eff";

      return (
        <span className="kuika-radio-group__radio" key={radioElementId}>
          <input
            id={radioElementId}
            type={inputType}
            value={optionKey}
            name={radioGroupElementId}
            checked={isChecked}
            aria-checked={isChecked}
            onChange={() => this.handleChange(optionKey)}
          />
          <label htmlFor={radioElementId} style={labelStyles}>
            {multiselectable ? this.checkbox(colorToUse, isChecked) : this.radio(colorToUse)}
            {optionText}
          </label>
        </span>
      );
    });
  };

  render(): ReactNode {
    return (
      <div
        className={`kuika-radio-group ${this.props.className ?? ""}`.trim()}
        style={this.getStyles()}
        id={this.props.id}
      >
        {this.getRadioElements()}
      </div>
    );
  }
}

const radioGroup = withCommonEvents(RadioGroup);
export { radioGroup as RadioGroup };
